import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import {
  DirectSubordinateResponse,
  EnvironmentVariables,
  Permission,
  SavedFilterScreen,
  TaskAiEvaluationResponse,
  UserConfigurationsResponse,
  WhatsappInstance,
} from "@/modules/common/types";
import {
  CmsDataResponse,
  GetUsersListResponse,
  LocationsResponse,
  PropertyTypeResponse,
  StatusCategoriesResponse,
  UpdateUserConfig,
  UserAvaililityResponse,
  UsersList,
  UserOrganization,
  LookupsResponse,
  LookupsParams,
  StatusesLookupsParams,
  UserSavedFiltersDto,
} from "@/modules/common/dtos/index.v2";
import { UserResponse } from "@/modules/agents/dtos";
import { AxiosPromise } from "axios";
import { LeadSearchFilters } from "@/modules/leads/types";

export const getPaginatedAssignedUsers = async (
  params: any,
  organizationUId?: string,
) => {
  const apiUrl = brandUrl<GetUsersListResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/users`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
        ...(organizationUId && { organizationUId }),
      },
      ...(!organizationUId && { includeSingleOrganization: true }),
    },
  }) as Promise<{
    data: {
      results: UsersList[];
      total: number;
      page: number;
      pageSize: number;
    };
  }>;
};

export const getPaginatedReferralAgents = async (
  params: object,
  organizationUId?: string,
) => {
  const apiUrl = brandUrl<GetUsersListResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/users/referral-agents`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
        ...(organizationUId && { organizationUId }),
      },
    },
  }) as Promise<GetUsersListResponse>;
};
export const getStatusCategories = async (params: object) => {
  const apiUrl = brandUrl<StatusCategoriesResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/status-categories`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<StatusCategoriesResponse>;
};

export const getLocations = async (params: any) => {
  const apiUrl = brandUrl<LocationsResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/locations`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<LocationsResponse>;
};

export const getCampaigns = async (
  params: object,
  parsedFilters?: LeadSearchFilters,
) => {
  const apiUrl = brandUrl<CmsDataResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/campaigns`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...(parsedFilters?.campaignUIds && {
          savedUIds: parsedFilters?.campaignUIds,
        }),
        ...params,
      },
    },
  }) as Promise<CmsDataResponse>;
};

export const getAds = async (params: object) => {
  const apiUrl = brandUrl<CmsDataResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/ads`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<CmsDataResponse>;
};

export const getAdsets = async (
  params: object,
  parsedFilters?: LeadSearchFilters,
) => {
  const apiUrl = brandUrl<CmsDataResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/adsets`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...(parsedFilters?.adsetUIds && {
          savedUIds: parsedFilters?.adsetUIds,
        }),
        ...params,
      },
    },
  }) as Promise<CmsDataResponse>;
};

export const getPropertyTypesV2 = async () => {
  const apiUrl = brandUrl<PropertyTypeResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/property-types`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<PropertyTypeResponse>;
};

export const updateUserConfig = async (
  userId: string,
  update: UpdateUserConfig,
) => {
  const apiUrl = brandUrl<UserResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/user-configurations/${userId}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.PATCH,
      data: { ...update },
    },
  }) as Promise<UserResponse>;
};

export const fetchUserPassportAPI = async (): Promise<{
  data: {
    permissions: Permission[];
    whatsappInstance: WhatsappInstance | null;
  };
}> => {
  const apiUrl = brandUrl<{
    data: {
      permissions: Permission[];
      whatsappInstance: WhatsappInstance | null;
    };
  }>(`${EnvironmentVariables.UMS_API_URL_V2}/admin/permissions/my-passport`);

  const res: {
    data: {
      permissions: Permission[];
      whatsappInstance: WhatsappInstance | null;
    };
  } = await fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  });
  return res;
};

export const fetchWhatsappQrCode = async (
  userUId: string,
): Promise<{
  data: { base64: string };
}> => {
  const apiUrl = brandUrl<{ data: { base64: string } }>(
    `${EnvironmentVariables.UMS_API_URL}/whatsapp-instances/${userUId}/qr-code`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: { base64: string } }>;
};

export const getUserFeatureFlags = async (): Promise<{
  data: Record<string, boolean>;
}> => {
  const apiUrl = brandUrl<Record<string, boolean>>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/feature-flags/user`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: Record<string, boolean> }>;
};

export const fetchUserOrganizations = async (
  userUId?: string,
  params?: { managerUIds?: string[]; page?: number; pageSize?: number },
) => {
  const apiUrl = brandUrl<{
    data: UserOrganization;
  }>(`${EnvironmentVariables.UMS_API_URL_V2}/users/${userUId}/organizations`);
  const urlParams = new URLSearchParams();

  if (params?.managerUIds && params?.managerUIds?.length > 0) {
    const baseUrl = `${EnvironmentVariables.UMS_API_URL}/organizations`;
    params.managerUIds.forEach((id) => urlParams.append("managerUIds", id));
    const fullUrl = `${baseUrl}?${urlParams.toString()}`;

    return fetchFromBrandedUrl({
      options: {
        brandedUrl: brandUrl<{ data: UserOrganization }>(fullUrl),
        isEncoded: false,
        method: AxiosRequestConfigMethods.GET,
      },
    }) as Promise<{ data: UserOrganization }>;
  }
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: UserOrganization }>;
};

export const fetchUserAccountManagers = async (params?: {
  manageOrganizationUIds?: string[];
  includeTags?: string | string[];
  page?: number;
  pageSize?: number;
  isManager?: boolean;
}) => {
  const baseUrl = `${EnvironmentVariables.UMS_API_URL_V2}/users`;
  const urlParams = new URLSearchParams();

  if (params?.page !== undefined) urlParams.append("page", String(params.page));
  if (params?.pageSize !== undefined)
    urlParams.append("pageSize", String(params.pageSize));
  if (params?.isManager !== undefined)
    urlParams.append("isManager", String(params.isManager));

  if (params?.manageOrganizationUIds) {
    params.manageOrganizationUIds.forEach((id) =>
      urlParams.append("manageOrganizationUIds", id),
    );
  }
  if (params?.includeTags) {
    urlParams.append("includeTags", String(params.includeTags));
  }

  const fullUrl = `${baseUrl}?${urlParams.toString()}`;

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: brandUrl<{ data: UserOrganization }>(fullUrl),
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: UserOrganization }>;
};

export const getUserAvailabilityV2 = async (
  userUid: string | undefined,
): Promise<UserAvaililityResponse> => {
  const apiUrl = brandUrl(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/user-configurations/${userUid}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<UserAvaililityResponse>;
};

export const getUsersUms = async (params: object) => {
  const apiUrl = brandUrl<GetUsersListResponse>(
    `${EnvironmentVariables.UMS_API_URL_V2}/users`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
      includeSingleOrganization: true,
    },
  }) as Promise<GetUsersListResponse>;
};

export const getUserConfiguration = async (userUid: string) => {
  const apiUrl = brandUrl<UserConfigurationsResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/user-configurations/${userUid}`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<UserConfigurationsResponse>;
};

export const getDirectSubordinates = async ({
  userId,
  params,
  organizationUId,
}: {
  userId: string;
  params?: {
    search?: string;
    page?: number;
    pageSize?: number;
    rankUIds?: string[];
  };
  organizationUId?: string | null;
}) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/users/${userId}/direct-subordinates`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
        ...(organizationUId && { organizationUId }),
      },
      includeSingleOrganization: true,
    },
  }) as AxiosPromise<DirectSubordinateResponse>;
};

export const fetchStatusesLookups = async (params?: StatusesLookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/statuses/statuses-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchMethodOfContactsLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/methods-of-contact/method-of-contact-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchAdsetsLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/adsets/adset-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchLeadTypesLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-types/lead-types-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchCampaignsLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/campaigns/campaigns-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchUsersLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/users/users-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchLeadSourcesLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/lead-sources/lead-source-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchStatusCategoriesLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl<DirectSubordinateResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/status-categories/status-categories-filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};

export const fetchSavedFilters = async (params: {
  screen: SavedFilterScreen;
  organizationUId?: string;
}) => {
  const apiUrl = brandUrl<UserSavedFiltersDto[]>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/saved-filters`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: UserSavedFiltersDto[] }>;
};

/**
 * Evaluate a task using AI
 */
export const evaluateTask = async (
  taskContent: string,
): Promise<TaskAiEvaluationResponse> => {
  const apiUrl = brandUrl<TaskAiEvaluationResponse>(
    `${EnvironmentVariables.NEW_CRM_BASE_API_URL}/meetings/tasks/evaluate`,
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.POST,
      data: { taskContent },
    },
  });
};

/**
 * Fetch tags lookups
 */
export const fetchTagsLookups = async (params?: LookupsParams) => {
  const apiUrl = brandUrl(`${EnvironmentVariables.NEW_CRM_BASE_API_URL}/tags`);

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        ...params,
      },
    },
  }) as Promise<{ data: LookupsResponse }>;
};
